import { MapPin, Phone, Mail } from "lucide-react";
import { useLang } from "@/i18n";
import Reveal from "./Reveal";

const MAP_SRC =
  "https://www.google.com/maps?q=Calle+Avil%C3%A9s+3,+46139+La+Pobla+de+Farnals,+Valencia&z=12&output=embed";

const MapSection = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="zona"
      data-testid="map-section"
      className="border-t border-black/10 bg-travertine py-28 lg:py-36"
    >
      <div className="mx-auto grid max-w-7xl gap-14 px-5 lg:grid-cols-5 lg:gap-20 lg:px-10">
        <Reveal className="lg:col-span-2">
          <p
            data-testid="map-eyebrow"
            className="font-mono text-[11px] uppercase tracking-[0.3em] text-terracotta"
          >
            {t.map.eyebrow}
          </p>
          <h2
            data-testid="map-heading"
            className="mt-5 font-display text-3xl font-medium leading-tight tracking-tight text-charcoal sm:text-4xl"
          >
            {t.map.heading}
          </h2>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-stone-500">
            {t.map.sub}
          </p>

          <ul className="mt-10 space-y-5" data-testid="map-contact-list">
            <li className="flex items-start gap-3">
              <MapPin size={15} className="mt-0.5 shrink-0 text-terracotta" />
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">
                  {t.map.baseLabel}
                </p>
                <p className="mt-1 text-sm text-charcoal" data-testid="map-address">
                  Calle Avilés 3, La Pobla de Farnals (46139), Valencia
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <Phone size={15} className="mt-0.5 shrink-0 text-terracotta" />
              <a href="tel:+34622669378" className="text-sm text-charcoal transition-colors hover:text-terracotta" data-testid="map-phone-link">
                +34 622 66 93 78
              </a>
            </li>
            <li className="flex items-start gap-3">
              <Mail size={15} className="mt-0.5 shrink-0 text-terracotta" />
              <a href="mailto:martinezamd95@gmail.com" className="text-sm text-charcoal transition-colors hover:text-terracotta" data-testid="map-email-link">
                martinezamd95@gmail.com
              </a>
            </li>
          </ul>

          <div className="mt-10 border-t border-black/10 pt-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-stone-400">
              {t.map.areaLabel}
            </p>
            <p className="mt-3 flex flex-wrap gap-x-2 gap-y-2 text-sm text-charcoal" data-testid="map-area-towns">
              {t.map.towns.map((town, i) => (
                <span key={`${lang}-town-${i}`} className="inline-flex items-center gap-2">
                  <span className="h-1 w-1 rotate-45 bg-terracotta" />
                  {town}
                </span>
              ))}
            </p>
          </div>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-3">
          <div className="relative">
            <div className="absolute -left-3 -top-3 h-full w-full border border-terracotta/50" aria-hidden="true" />
            <div className="relative overflow-hidden border border-black/10 bg-charcoal">
              <iframe
                data-testid="map-iframe"
                title={t.map.heading}
                src={MAP_SRC}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="h-[420px] w-full grayscale-[35%] contrast-[1.05] lg:h-[520px]"
              />
              <span className="pointer-events-none absolute bottom-3 left-3 bg-charcoal/85 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-stone-300 backdrop-blur-sm">
                39.581°N · 0.324°W
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default MapSection;
